/**
 * Onboarding product-orientation step (APP-002 §9).
 *
 * A compact orientation showing the basic workflow and honest links to the API
 * Keys, Playground, and Quickstart documentation. The API Keys and Playground
 * pages are now real (APP-004, APP-005); the Playground runs under the
 * dashboard session, so it can be tried without a key.
 */
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Key, Send, ShieldCheck, Gauge, BookOpen, Sparkles, ArrowRight } from '@/components/icons';
import { getAppLinks } from '@/config/links';

const STEPS = [
  {
    icon: Key,
    title: 'Create an API key',
    body: 'Generate a reveal-once key with the scopes your application needs.',
  },
  {
    icon: Send,
    title: 'Send a signup for analysis',
    body: 'POST the signup payload to the analyze endpoint from your backend.',
  },
  {
    icon: ShieldCheck,
    title: 'Use the decision and checks',
    body: 'Act on the risk decision, score, band, and checks in your product logic.',
  },
  {
    icon: Gauge,
    title: 'Monitor usage and access',
    body: 'Track quota, manage keys, and review activity from this dashboard.',
  },
];

export function OrientationStep() {
  const links = getAppLinks();
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">How it works</h2>
        <p className="text-sm text-muted-foreground">
          Four steps from key to decision. Create an API key to integrate, or try the Playground with your dashboard
          session first.
        </p>
      </div>
      <ol className="space-y-3">
        {STEPS.map((step, index) => (
          <li key={step.title}>
            <Card className="border-border/60">
              <CardContent className="flex items-start gap-3 p-4">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {index + 1}
                </div>
                <div className="flex flex-1 items-start gap-3">
                  <step.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="text-xs text-muted-foreground">{step.body}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </li>
        ))}
      </ol>
      <div className="grid gap-3 sm:grid-cols-3">
        <Link
          to="/api-keys"
          className="group flex items-center justify-between rounded-lg border border-border/60 p-3 text-sm transition-colors hover:border-primary/40 hover:bg-accent"
        >
          <span className="flex items-center gap-2">
            <Key className="h-4 w-4 text-primary" />
            API Keys
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Open
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
        <Link
          to="/playground"
          className="group flex items-center justify-between rounded-lg border border-border/60 p-3 text-sm transition-colors hover:border-primary/40 hover:bg-accent"
        >
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Playground
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            Open
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </Link>
        <a
          href={links.docsBase}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center justify-between rounded-lg border border-border/60 p-3 text-sm transition-colors hover:border-primary/40 hover:bg-accent"
        >
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            Quickstart
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </a>
      </div>
    </div>
  );
}
